import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
    Calendar,
    ChevronLeft,
    Clock,
    GraduationCap,
    Trash2,
    Users,
} from 'lucide-react';
import RecurrenceManager from './RecurrenceManager';
import { describeStoredRecurrenceDays } from '../../../utils/weekdaySchedule';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

export default function CoursesList({ courses, onCourseUpdated, onCourseDeleted, readOnly = false }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 767px)');

    const [selectedCourse, setSelectedCourse] = useState(null);
    const [detailTab, setDetailTab] = useState('dates');

    useEffect(() => {
        if (!courses?.length) {
            setSelectedCourse(null);
            return;
        }

        setSelectedCourse((prev) => {
            if (prev) {
                const updated = courses.find((course) => Number(course.id) === Number(prev.id));
                if (updated) return updated;
            }
            // Desktop: seleziona il primo; mobile: nessuna preselezione
            return isMobile ? null : courses[0];
        });
    }, [courses, isMobile]);

    const handleDelete = async (course) => {
        if (readOnly || !course) return;
        if (!window.confirm(t('gym.courses.list.deleteConfirm', { title: course.title }))) {
            return;
        }

        try {
            const api = getAuthenticatedApi();
            await api.delete(`/gym_courses.php?action=delete_course&id=${course.id}`);
            toast.success(t('gym.courses.list.deleteSuccess'));
            onCourseDeleted(course.id);
            setSelectedCourse(null);
        } catch (err) {
            console.error('Errore eliminazione corso:', err);
            toast.error(t('gym.courses.errors.deleteError'));
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-800 dark:text-green-400', label: t('gym.courses.status.active') },
            paused: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', text: 'text-yellow-800 dark:text-yellow-400', label: t('gym.courses.status.paused') },
            cancelled: { bg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-800 dark:text-red-400', label: t('gym.courses.status.cancelled') }
        };

        const badge = badges[status] || badges.active;

        return (
            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
                {badge.label}
            </span>
        );
    };

    const getRecurrenceText = (course) => {
        if (!course.is_recurring) return t('gym.courses.list.notRecurring');

        const daysMap = {
            monday: t('gym.calendar.days.monday'),
            tuesday: t('gym.calendar.days.tuesday'),
            wednesday: t('gym.calendar.days.wednesday'),
            thursday: t('gym.calendar.days.thursday'),
            friday: t('gym.calendar.days.friday'),
            saturday: t('gym.calendar.days.saturday'),
            sunday: t('gym.calendar.days.sunday')
        };

        if (course.recurrence_days && Array.isArray(course.recurrence_days)) {
            const days = describeStoredRecurrenceDays(
                course.recurrence_days,
                (dayKey) => daysMap[dayKey] || dayKey
            );
            return days || t('gym.courses.list.recurring');
        }

        return t('gym.courses.list.recurring');
    };

    const getCapacityText = (course) => (
        course.is_unlimited
            ? t('gym.courses.list.unlimited')
            : (course.max_participants || t('gym.courses.list.notSet'))
    );

    const getScheduleText = (course) => {
        if (course.is_recurring && course.standard_start_time) {
            return `${course.standard_start_time?.slice(0, 5)} - ${course.standard_end_time?.slice(0, 5)}`;
        }
        return null;
    };

    const showList = !isMobile || !selectedCourse;
    const showDetail = !isMobile || Boolean(selectedCourse);

    const courseList = (
        <div className={`${isMobile ? 'flex h-full min-h-0 flex-col' : 'col-span-3 flex h-full min-h-0 flex-col'} border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800`}>
            <div className="flex-1 overflow-y-auto dark:bg-gray-900">
                {courses.length > 0 ? (
                    <div className="space-y-2 p-2">
                        {courses.map((course) => {
                            const isSelected = Number(selectedCourse?.id) === Number(course.id);
                            return (
                                <button
                                    key={course.id}
                                    type="button"
                                    onClick={() => {
                                        setSelectedCourse(course);
                                        setDetailTab('dates');
                                    }}
                                    className={`w-full rounded-lg border-2 p-3 text-left transition-colors ${
                                        isSelected
                                            ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-950/50'
                                            : 'border-transparent hover:bg-gray-50 dark:hover:bg-white/10'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        {course.logo_filename ? (
                                            <img
                                                src={`/api/serve_image.php?filename=${course.logo_filename}&type=logo`}
                                                alt=""
                                                className="h-9 w-9 flex-shrink-0 rounded-lg border border-gray-200 object-cover dark:border-white/15"
                                            />
                                        ) : (
                                            <div
                                                className="mt-1.5 h-3 w-3 flex-shrink-0 rounded-full"
                                                style={{ backgroundColor: course.color || '#3B82F6' }}
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                                                    {course.title}
                                                </p>
                                                {getStatusBadge(course.status)}
                                            </div>
                                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                {course.category && (
                                                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-white/80">
                                                        {course.category}
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-gray-500 dark:text-white/55">
                                                    Max {getCapacityText(course)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center px-4 py-10 text-center text-gray-500 dark:text-white/70">
                        <GraduationCap size={40} className="mb-3 opacity-50" />
                        <p className="text-sm font-medium">{t('gym.courses.emptyState.noCoursesFound')}</p>
                    </div>
                )}
            </div>
        </div>
    );

    const readOnlyDetail = selectedCourse ? (
        <div className="flex h-full min-h-0 flex-col bg-white dark:bg-gray-800">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-4 dark:border-white/10 md:px-6">
                {isMobile && (
                    <button
                        type="button"
                        onClick={() => setSelectedCourse(null)}
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10"
                        aria-label={t('gym.courses.list.backToList')}
                    >
                        <ChevronLeft size={22} />
                    </button>
                )}
                <div className="min-w-0">
                    <h3 className="truncate text-xl font-semibold text-gray-900 dark:text-white">
                        {selectedCourse.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
                        {selectedCourse.category || t('gym.courses.list.recurrence')}
                    </p>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <InfoItem
                        icon={<Users size={16} />}
                        label={t('gym.courses.list.maxParticipants')}
                        value={getCapacityText(selectedCourse)}
                    />
                    <InfoItem
                        icon={<Calendar size={16} />}
                        label={t('gym.courses.list.sessions')}
                        value={`${selectedCourse.upcoming_sessions || 0} / ${selectedCourse.total_sessions || 0}`}
                    />
                    <InfoItem
                        icon={<Clock size={16} />}
                        label={t('gym.courses.list.recurrence')}
                        value={getRecurrenceText(selectedCourse)}
                    />
                    {getScheduleText(selectedCourse) && (
                        <InfoItem
                            icon={<Clock size={16} />}
                            label={t('gym.courses.list.schedule')}
                            value={getScheduleText(selectedCourse)}
                        />
                    )}
                </div>
                <div className="mt-6 border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-white/10 dark:text-white/60">
                    <p>{t('gym.courses.list.createdBy')}: {selectedCourse.created_by_name || 'N/A'}</p>
                    <p className="mt-1">
                        {t('gym.courses.list.createdAt')}:{' '}
                        {new Date(selectedCourse.created_at).toLocaleDateString('it-IT')}
                    </p>
                </div>
            </div>
        </div>
    ) : null;

    const detailPanel = (
        <div className={`${isMobile ? 'flex h-full min-h-0 flex-col' : 'col-span-9 flex h-full min-h-0 flex-col'} overflow-hidden bg-white dark:bg-gray-800`}>
            {selectedCourse ? (
                readOnly ? (
                    readOnlyDetail
                ) : (
                    <RecurrenceManager
                        embedded
                        isOpen
                        course={selectedCourse}
                        initialTab={detailTab}
                        onClose={isMobile ? () => setSelectedCourse(null) : undefined}
                        onRecurrenceUpdated={onCourseUpdated}
                        headerActions={(
                            <button
                                type="button"
                                onClick={() => handleDelete(selectedCourse)}
                                className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                                title={t('gym.courses.list.delete')}
                            >
                                <Trash2 size={18} />
                            </button>
                        )}
                    />
                )
            ) : (
                <div className="flex h-full flex-col items-center justify-center px-6 text-center text-gray-500 dark:text-white/70">
                    <GraduationCap size={48} className="mb-4 opacity-40" />
                    <p className="font-medium">{t('gym.courses.selectCourse')}</p>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <div className="h-full min-h-0 overflow-hidden rounded-lg border border-gray-200 dark:border-white/10">
                {showList ? courseList : null}
                {showDetail ? detailPanel : null}
            </div>
        );
    }

    return (
        <div className="grid h-full min-h-0 grid-cols-12 overflow-hidden rounded-lg border border-gray-200 dark:border-white/10">
            {courseList}
            {detailPanel}
        </div>
    );
}

function InfoItem({ icon, label, value }) {
    return (
        <div className="flex items-start gap-2">
            <div className="mt-0.5 text-gray-400 dark:text-gray-500">
                {icon}
            </div>
            <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{value}</p>
            </div>
        </div>
    );
}
